// This file renders terminal audits as the landing page history ledger. It owns display sorting, compact status styling, and the local five-row expansion.

import { ArrowDown, ChevronRight, FileText, Globe2 } from 'lucide-react'
import { motion } from 'motion/react'
import { useState } from 'react'
import { Link } from 'react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { staggerContainer, staggerItem } from '@/lib/motion/variants'
import type { SiteHealthAuditListItem } from '@/lib/siteHealth.types'
import { useTrackerHref } from '@/provider/useTrackerHref'
import { displayDate } from './SiteHealthFormat'
import type { SiteHealthAuditHistoryProps } from './siteHealthComponents.types'

const COLLAPSED_AUDIT_COUNT = 5

function statusLabel(audit: SiteHealthAuditListItem): string {
  if (audit.status === 'complete') return 'Complete'

  if (audit.status === 'partial') return 'Partial'

  return 'Failed'
}

function statusClassName(audit: SiteHealthAuditListItem): string {
  if (audit.status === 'complete') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300'
  }

  if (audit.status === 'error') {
    return 'border-destructive/30 bg-destructive/10 text-destructive'
  }

  return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300'
}

function scoreClassName(audit: SiteHealthAuditListItem): string {
  if (audit.status === 'complete') return 'text-emerald-600'

  if (audit.status === 'partial') return 'text-amber-600'

  return 'text-muted-foreground'
}

function siteLabel(url: string): string {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

function newestFirst(audits: SiteHealthAuditListItem[]) {
  return [...audits].sort(
    (left, right) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
  )
}

export default function SiteHealthAuditHistory({
  audits,
  loading,
  error,
}: SiteHealthAuditHistoryProps) {
  const [showAll, setShowAll] = useState(false)
  const href = useTrackerHref()
  const sortedAudits = newestFirst(audits)
  const visibleAudits = showAll
    ? sortedAudits
    : sortedAudits.slice(0, COLLAPSED_AUDIT_COUNT)

  return (
    <section
      aria-labelledby="site-health-history-heading"
      className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
    >
      <div className="border-b border-border px-6 py-5 sm:px-8">
        <h2
          id="site-health-history-heading"
          className="text-xl font-semibold tracking-tight"
        >
          Audit history
        </h2>
      </div>

      {loading && (
        <p className="px-6 py-10 text-center text-sm text-muted-foreground">
          Loading audits…
        </p>
      )}
      {error && (
        <p className="px-6 py-10 text-center text-sm text-destructive">
          Audit history could not be loaded.
        </p>
      )}
      {!loading && !error && visibleAudits.length === 0 && (
        <p className="px-6 py-10 text-center text-sm text-muted-foreground">
          No completed audits yet. Start an audit to build your history.
        </p>
      )}
      {visibleAudits.length > 0 && (
        <div className="overflow-x-auto">
          <table
            aria-label="Audit history"
            className="w-full min-w-[820px] text-left"
          >
            <thead className="bg-muted/30 text-xs uppercase tracking-[0.14em] text-primary">
              <tr>
                <th scope="col" className="px-6 py-3.5 font-medium sm:px-8">
                  Site
                </th>
                <th scope="col" className="px-5 py-3.5 font-medium">
                  Scope
                </th>
                <th scope="col" className="px-5 py-3.5 font-medium">
                  <span className="inline-flex items-center gap-1.5">
                    Date
                    <ArrowDown className="size-3" aria-hidden="true" />
                  </span>
                </th>
                <th scope="col" className="px-5 py-3.5 font-medium">
                  Pages
                </th>
                <th scope="col" className="px-5 py-3.5 font-medium">
                  Status
                </th>
                <th scope="col" className="px-5 py-3.5 font-medium">
                  Score
                </th>
                <th scope="col" className="w-12 px-5 py-3.5">
                  <span className="sr-only">Open report</span>
                </th>
              </tr>
            </thead>
            <motion.tbody
              key={showAll ? 'all' : 'collapsed'}
              variants={staggerContainer}
              initial="hidden"
              animate="shown"
              className="divide-y divide-border"
            >
              {visibleAudits.map((audit) => {
                const ScopeIcon =
                  audit.scope === 'single_page' ? FileText : Globe2

                return (
                  <motion.tr
                    key={audit.auditId}
                    variants={staggerItem}
                    className="group relative transition-colors hover:bg-muted/30 focus-within:bg-muted/30"
                  >
                    <td className="px-6 py-4 sm:px-8">
                      <Link
                        to={href(`/site-health/${audit.auditId}`)}
                        aria-label={`View report for ${audit.submittedUrl}`}
                        className="font-medium text-primary outline-none after:absolute after:inset-0"
                      >
                        {siteLabel(audit.submittedUrl)}
                      </Link>
                    </td>
                    <td className="px-5 py-4 text-sm text-muted-foreground">
                      <span className="inline-flex items-center gap-2">
                        <ScopeIcon className="size-4" aria-hidden="true" />
                        {audit.scope === 'single_page' ? 'Single page' : 'Site'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm tabular-nums text-muted-foreground">
                      {displayDate(audit.finishedAt ?? audit.createdAt)}
                    </td>
                    <td className="px-5 py-4 text-sm tabular-nums text-muted-foreground">
                      {audit.auditedPages}
                    </td>
                    <td className="px-5 py-4">
                      <Badge
                        variant="outline"
                        className={statusClassName(audit)}
                      >
                        {statusLabel(audit)}
                      </Badge>
                    </td>
                    <td
                      className={`px-5 py-4 text-sm font-semibold tabular-nums ${scoreClassName(audit)}`}
                    >
                      {audit.score === null ? '—' : Math.round(audit.score)}
                    </td>
                    <td className="px-5 py-4 text-muted-foreground">
                      <ChevronRight
                        className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
                        aria-hidden="true"
                      />
                    </td>
                  </motion.tr>
                )
              })}
            </motion.tbody>
          </table>
        </div>
      )}

      {!showAll && sortedAudits.length > COLLAPSED_AUDIT_COUNT && (
        <div className="flex justify-center border-t border-border px-6 py-4">
          <Button
            type="button"
            variant="ghost"
            className="text-primary"
            onClick={() => setShowAll(true)}
          >
            Show all audits
          </Button>
        </div>
      )}
    </section>
  )
}
